import { Config } from "../../Config";
import {
    DataUnavailableDictionaryDefinition
} from "../../stores/dictionary/DataUnavailableDictionaryDefinition";
import { DictionaryDefinition } from "../../stores/dictionary/DictionaryDefinition";
import { Word } from "../../stores/word/Word";
import { DictionaryResponseParserFromText } from "./DictionaryResponseParserFromText";
import { IDictionaryService } from "./IDictionaryService";

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_IN_MILLIS = 1000;

export class DictionaryService implements IDictionaryService {
  getDefinition(
    word: Word,
    callback: (definition: DictionaryDefinition) => void
  ) {
    const noDataDefinition = new DataUnavailableDictionaryDefinition(word.word);

    const url = this.getProxyUrl(this.fixWordForService(word.word));

    let attempt = 0;

    const retryFetch = () => {
      if (attempt < MAX_ATTEMPTS) {
        // first retry is almost immediate (since browser has already waited some time)
        const delay = RETRY_DELAY_IN_MILLIS * Math.pow(3, attempt);
        attempt++;

        console.log(
          `[dict] retry '${word.word}' [${attempt}] in ${delay}ms...`
        );
        setTimeout(() => {
          console.log(`[dict] retrying '${word.word}'`);
          tryFetch();
        }, delay);
      } else {
        console.warn(`[dict] '${word.word}' giving up`);
        callback(noDataDefinition);
      }
    };

    const tryFetch = () => {
      fetch(url)
        .then(
          response =>
            response
              .text()
              .then(str => {
                return this.processDefinitionText(word, str);
              })
              .then(
                defn => {
                  console.log(`[dict] '${word.word}' [ok]`);
                  callback(defn);
                },
                jsonRejected => {
                  console.error("(json)", jsonRejected);
                  callback(noDataDefinition);
                }
              ),
          rejectedReason => {
            console.error("(rejected)", rejectedReason);

            retryFetch();
          }
        )
        .catch(error => {
          console.error("(exception!)", error);
          callback(noDataDefinition);
        });
    };

    tryFetch();
  }

  private fixWordForService(word: string): string {
    return word.replace(" ", "");
  }

  private processDefinitionText(
    word: Word,
    text: string
  ): Promise<DictionaryDefinition> {
    return new Promise((resolve, reject) => {
      try {
        const defn = DictionaryResponseParserFromText.parseXmlFromText(
          word,
          text
        );
        resolve(defn);
      } catch (error) {
        reject(error);
      }
    });
  }

  // php proxy is easier to deploy !
  // need a local WS to act as proxy to talk to the cross-origin XML WS :-(
  private getProxyUrlUsingPhp(word) {
    // we need to allow for app sitting under a sub-folder.

    // assuming that the app specific part of URL starts with #:

    const getBaseUrl = () => {
      const url = window.location.href;
      const hashIndex = url.indexOf("/#");
      return url.substr(0, hashIndex);
    };

    // deploying .NET proxy hits security blocks, but no such problem with php :-)
    // in any case we restrict requests to that server
    return getBaseUrl() + "/dictProxy.php" + "?word=" + word;
  }

  private getProxyUrl(word) {
    // request directly if local dev (CORS off)
    // or via php proxy if in production
    return this.isUsingPhp()
      ? this.getProxyUrlUsingPhp(word)
      : this.getUrlForDictionary(word);
  }

  private isUsingPhp() {
    return !Config.isLocalDev();
  }

  // ref: http://www.dictionaryapi.com/
  /*examples:
    dictionary:
    http://www.dictionaryapi.com/api/v1/references/collegiate/xml/wonderful?key=43ff6ed6-94a1-4e5d-881a-e85a37550217

    thesaurus:
    http://www.dictionaryapi.com/api/v1/references/thesaurus/xml/wonderful?key=31d8e1ad-bb4a-4a41-93ea-8c0a109620db
  */
  private getUrlForDictionary(word) {
    return `http://www.dictionaryapi.com/api/v1/references/collegiate/xml/${word}?key=43ff6ed6-94a1-4e5d-881a-e85a37550217`;
  }

  // TODO use thesaurus!
  //   private getUrlForThesaurus(word) {
  //     return `http://www.dictionaryapi.com/api/v1/references/thesaurus/xml/${word}?key=31d8e1ad-bb4a-4a41-93ea-8c0a109620db`;
  //   }
}



// WEBPACK FOOTER //
// ./services/dictionary/DictionaryService.ts